import type { AnalysisResult } from '../lib/types';

export const mock10K: AnalysisResult = {
  slug: 'sample-10k-mdna',
  title: 'Microsoft 10-K MD&A — FY26',
  date: '2026-08-15T20:00:00Z',
  documentType: '10-k-mdna',
  verdict: 'MIXED',
  score: 0.12,
  confidence: 72,
  summary:
    'Defensive posture on AI capex with offsetting strength in cloud. Hedged language across regulatory, competitive, and macro sections.',
  briefing: {
    overall:
      'Tone shifts from confident in the cloud + AI commercial discussion to cautious in the regulatory and capital sections. Forward statements lean conservative; numerical claims are detailed and cite-aligned.',
    dimensionBreakdown: [
      { label: 'Tone & Confidence', copy: 'Confidence concentrated in Azure and AI commercial uptake; defensive in capex and antitrust sections.' },
      { label: 'Forward-Looking Posture', copy: 'Guidance ranges widened on operating margin; capex envelope raised but with caveats.' },
      { label: 'Hedging & Qualifiers', copy: 'Elevated. Particularly in capital allocation and regulatory disclosures.' },
      { label: 'Risk Disclosure', copy: 'Significant expansion in cyber and AI safety risk language vs. prior year.' },
      { label: 'Quantification', copy: 'Heavy. 96 quantitative claims across the MD&A.' },
    ],
    entityLandscape: 'Azure, M365 Copilot, and gaming pull positive; Activision integration and EU regulators pull negative.',
    trajectory: 'Sentiment opens balanced, peaks in cloud commentary, troughs in capex / regulatory, and recovers modestly in the outlook block.',
    linguisticProfile: 'Higher hedge density vs. Q4 prior. More passive voice in legal disclosures. Modal verbs skew conditional.',
  },
  kpi: { confidence: 72, redFlags: 6, entities: 41, sections: 14 },
  bullSignals: [
    { text: 'Azure +28% YoY constant currency with AI workloads now 35% of incremental growth.', paragraphRef: 'P22' },
    { text: 'Operating margin expansion of 180bps despite the AI capex ramp.', paragraphRef: 'P31' },
    { text: 'M365 Copilot attach rate at 19% of E3+ seats; ARPU expansion explicit.', paragraphRef: 'P28' },
  ],
  bearSignals: [
    { text: 'AI capex envelope raised but capacity constraints flagged through FY27.', paragraphRef: 'P34' },
    { text: 'Heightened regulatory risk language across EU, UK, and US sections.', paragraphRef: 'P52' },
    { text: 'Integration cost overruns on Activision flagged with quantified margin headwind.', paragraphRef: 'P41' },
  ],
  dimensions: [
    { label: 'Tone & Confidence', value: 0.58, qualifier: 'ELEVATED' },
    { label: 'Forward-Looking Posture', value: 0.42, qualifier: 'MODERATE' },
    { label: 'Hedging & Qualifiers', value: 0.61, qualifier: 'ELEVATED' },
    { label: 'Risk Disclosure', value: 0.78, qualifier: 'HIGH' },
    { label: 'Quantification', value: 0.84, qualifier: 'HIGH' },
  ],
  radar: [
    { label: 'Tone', value: 0.58 },
    { label: 'Forward-Looking', value: 0.42 },
    { label: 'Directness', value: 0.39 },
    { label: 'Risk Disclosure', value: 0.78 },
    { label: 'Quantification', value: 0.84 },
  ],
  entities: [
    { name: 'Azure', type: 'Product', verdict: 'BULLISH', score: 0.72, context: 'Workload mix tilting toward AI; 28% CC growth.' },
    { name: 'M365 Copilot', type: 'Product', verdict: 'BULLISH', score: 0.61, context: 'Attach rate ahead of internal plan.' },
    { name: 'Activision', type: 'Segment', verdict: 'BEARISH', score: -0.32, context: 'Integration cost overrun flagged with quantified margin drag.' },
    { name: 'EU Commission', type: 'Geography', verdict: 'BEARISH', score: -0.28, context: 'Heightened regulatory hedging across cloud and AI sections.' },
    { name: 'OpenAI Partnership', type: 'Company', verdict: 'MIXED', score: 0.08, context: 'Commercial cited positively; governance language widened.' },
    { name: 'Satya Nadella', type: 'Person', verdict: 'BULLISH', score: 0.45, context: 'Direct framing on AI commercial trajectory.' },
  ],
  sections: [
    { idx: 1, title: 'Business Overview', tags: ['boilerplate'], summary: 'Stable framing across segments.', range: 'P1–P8', verdict: 'MIXED', score: 0.05 },
    { idx: 2, title: 'Cloud & AI Commercial', tags: ['cloud', 'ai'], summary: 'Confident, quantified, forward-leaning.', range: 'P9–P22', verdict: 'BULLISH', score: 0.74 },
    { idx: 3, title: 'Productivity & Business Processes', tags: ['productivity'], summary: 'Copilot attach rate and ARPU detail.', range: 'P23–P30', verdict: 'BULLISH', score: 0.62 },
    { idx: 4, title: 'Capital Allocation', tags: ['capital'], summary: 'AI capex raised, capacity constrained.', range: 'P31–P38', verdict: 'MIXED', score: -0.05 },
    { idx: 5, title: 'Gaming & Activision', tags: ['gaming'], summary: 'Integration cost overrun flagged.', range: 'P39–P44', verdict: 'BEARISH', score: -0.34 },
    { idx: 6, title: 'Regulatory & Litigation', tags: ['risk'], summary: 'Heightened hedging and risk language.', range: 'P45–P58', verdict: 'BEARISH', score: -0.41 },
    { idx: 7, title: 'Outlook', tags: ['guidance'], summary: 'Wider operating margin range; cautious.', range: 'P59–P64', verdict: 'MIXED', score: 0.18 },
  ],
  qna: [],
  numerical: [
    { metric: 'Azure Revenue Growth', value: '28% CC', period: 'FY26', direction: 'up', framing: 'Acceleration vs FY25 (24%).', aligned: true },
    { metric: 'M365 Copilot Attach', value: '19%', period: 'FY26 exit', direction: 'up', framing: 'Ahead of internal plan.', aligned: true },
    { metric: 'AI Capex', value: '$78B', period: 'FY26', direction: 'up', framing: 'Raised; capacity constraints flagged.', aligned: true },
    { metric: 'Operating Margin', value: '44.1%', period: 'FY26', direction: 'up', framing: 'Up 180bps despite capex ramp.', aligned: true },
    { metric: 'Activision Integration', value: '-$1.2B', period: 'FY26', direction: 'down', framing: 'Quantified margin headwind.', aligned: true },
  ],
  linguisticIndicators: [
    { label: 'Hedge Density', value: '0.31', rawValue: 0.62, tooltip: 'Above 4-quarter average — concentrated in regulatory.' },
    { label: 'Forward Modals', value: '0.28', rawValue: 0.56 },
    { label: 'Active Voice', value: '0.78', rawValue: 0.78 },
    { label: 'Sentence Length', value: '24 words', rawValue: 0.62 },
    { label: 'Lexical Density', value: '0.66', rawValue: 0.66 },
    { label: 'Modal Confidence', value: '0.51', rawValue: 0.51 },
    { label: 'Quantification', value: '96 claims', rawValue: 0.84 },
    { label: 'Negation', value: '0.12', rawValue: 0.24 },
  ],
  fingerprint: [
    { label: 'Confidence Words', value: 0.58 },
    { label: 'Forward-Looking Verbs', value: 0.42 },
    { label: 'Quantified Claims', value: 0.84 },
    { label: 'Active Voice', value: 0.78 },
    { label: 'Concrete Nouns', value: 0.74 },
    { label: 'Hedges (inv.)', value: 0.39 },
    { label: 'Filler Phrases (inv.)', value: 0.71 },
    { label: 'Negation (inv.)', value: 0.76 },
  ],
  sectorLexicon: [
    { name: 'Cloud & Compute', matches: 88, pos: 52, neg: 12, risk: 24, score: 0.42 },
    { name: 'Software & Services', matches: 64, pos: 41, neg: 8, risk: 15, score: 0.51 },
    { name: 'Regulatory & Antitrust', matches: 38, pos: 4, neg: 22, risk: 12, score: -0.48 },
    { name: 'M&A / Integration', matches: 21, pos: 6, neg: 11, risk: 4, score: -0.28 },
  ],
  riskQuotes: [
    { quote: 'Capacity constraints could limit our ability to meet near-term Azure AI demand.', ref: 'P34', category: 'cost' },
    { quote: 'Regulatory developments in the European Union could materially affect our cloud business.', ref: 'P52', category: 'litigation' },
    { quote: 'Integration costs related to Activision are expected to exceed prior estimates.', ref: 'P41', category: 'cost' },
  ],
  verification: [
    { label: 'Numerical claims aligned with framing', status: 'pass', note: '94 of 96 aligned.' },
    { label: 'Forward-looking statements properly flagged', status: 'pass' },
    { label: 'Risk Factor Coverage', status: 'flag', note: 'AI safety language widened — review against prior 10-K.' },
    { label: 'Sector lexicon coverage', status: 'pass' },
    { label: 'Source paragraphs cite-aligned', status: 'pass' },
  ],
  advanced: [
    { key: 'Model Version', value: 'tia-sentiment-v3.4' },
    { key: 'Total Tokens', value: '38,442' },
    { key: 'Sections Detected', value: '14' },
    { key: 'Entities Detected', value: '41' },
    { key: 'Latency', value: '8.1s' },
    { key: 'Run ID', value: 'run_4mkb1zr2pq' },
  ],
};
